import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain,
  Zap,
  Target,
  Lightbulb,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  Layers,
  Users,
  Calendar,
  TrendingUp,
  Sparkles,
  RefreshCw,
  Download,
  Share
} from 'lucide-react';
import { trizPrinciples, demoCompanies, demoFeatures } from '../../data/demoData';
import { TRIZPrinciple, Feature, Company } from '../../types';

const RoadmapGenerator = () => {
  const [selectedCompany, setSelectedCompany] = useState<Company>(demoCompanies[0]);
  const [currentStep, setCurrentStep] = useState<'company' | 'analysis' | 'triz' | 'features' | 'roadmap'>('company');
  const [isGenerating, setIsGenerating] = useState(false);
  const [contradictions, setContradictions] = useState<Array<{improving: string, worsening: string, principles: number[]}>>([]);
  const [generatedFeatures, setGeneratedFeatures] = useState<Feature[]>([]);
  const [innovationScore, setInnovationScore] = useState(0);

  const trizContradictions = [
    { improving: 'Speed of development', worsening: 'Code quality', principles: [1, 15, 35] },
    { improving: 'Feature richness', worsening: 'User experience simplicity', principles: [2, 17, 35] },
    { improving: 'System scalability', worsening: 'Initial cost', principles: [1, 15, 2] },
    { improving: 'Security level', worsening: 'User convenience', principles: [17, 35, 15] }
  ];

  const handleTRIZAnalysis = async () => {
    setIsGenerating(true);
    setCurrentStep('analysis');
    
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setContradictions(trizContradictions);
    setCurrentStep('triz');
    
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const aiGeneratedFeatures: Feature[] = [
      {
        id: 'ai-feat-1',
        name: 'Modular AI Architecture',
        description: 'Segmented AI system with independent processing modules',
        priority: 'critical',
        effort: 9,
        impact: 10,
        category: 'AI Core',
        dependencies: [],
        estimatedHours: 480,
        assignedTeam: 'AI Engineering',
        status: 'backlog',
        quarter: 'Q1 2024'
      },
      {
        id: 'ai-feat-2',
        name: 'Adaptive User Interface',
        description: 'Dynamic UI that adapts to user skill level and preferences',
        priority: 'high',
        effort: 7,
        impact: 8,
        category: 'UX Innovation',
        dependencies: ['ai-feat-1'],
        estimatedHours: 320,
        assignedTeam: 'Frontend Team',
        status: 'backlog',
        quarter: 'Q2 2024'
      },
      {
        id: 'ai-feat-3',
        name: 'Multi-Dimensional Analytics',
        description: '3D visualization for complex data relationships',
        priority: 'high',
        effort: 8,
        impact: 9,
        category: 'Analytics',
        dependencies: ['ai-feat-1'],
        estimatedHours: 400,
        assignedTeam: 'Data Team',
        status: 'backlog',
        quarter: 'Q2 2024'
      },
      {
        id: 'ai-feat-4',
        name: 'Progressive Feature Disclosure',
        description: 'Smart feature revelation based on user competency',
        priority: 'medium',
        effort: 6,
        impact: 7,
        category: 'UX Innovation',
        dependencies: ['ai-feat-2'],
        estimatedHours: 240,
        assignedTeam: 'Product Team',
        status: 'backlog',
        quarter: 'Q3 2024'
      }
    ];
    
    setGeneratedFeatures(aiGeneratedFeatures);
    setInnovationScore(8.7);
    setCurrentStep('features');
    setIsGenerating(false);
  };

  const generateRoadmap = () => {
    setCurrentStep('roadmap');
  };

  const restartProcess = () => {
    setCurrentStep('company');
    setContradictions([]);
    setGeneratedFeatures([]);
    setInnovationScore(0);
    setIsGenerating(false);
  };

  return (
    <div className="h-full bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 p-6">
      <div className="max-w-7xl mx-auto h-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl sqsft-heading-1 mb-2">
                AI-Powered Roadmap Generator
              </h1>
              <p className="sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                Generate breakthrough product roadmaps using TRIZ methodology and AI innovation
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-lg p-3 border border-slate-200/50 dark:border-slate-700/50">
                <div className="flex items-center space-x-3">
                  <Brain className="w-5 h-5 text-purple-600" />
                  <span className="sqsft-small font-medium">
                    TRIZ Engine Active
                  </span>
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                </div>
              </div>
              
              {currentStep !== 'company' && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={restartProcess}
                  className="sqsft-btn-secondary flex items-center space-x-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Restart</span>
                </motion.button>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-4 mb-6">
            {['company', 'analysis', 'triz', 'features', 'roadmap'].map((step, index) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                  currentStep === step 
                    ? 'bg-blue-600 text-white' 
                    : index < ['company', 'analysis', 'triz', 'features', 'roadmap'].indexOf(currentStep)
                    ? 'bg-green-500 text-white'
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                }`}>
                  {index < ['company', 'analysis', 'triz', 'features', 'roadmap'].indexOf(currentStep) ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    index + 1
                  )}
                </div>
                
                {index < 4 && (
                  <div className={`w-12 h-0.5 mx-2 transition-all duration-300 ${
                    index < ['company', 'analysis', 'triz', 'features', 'roadmap'].indexOf(currentStep)
                      ? 'bg-green-500'
                      : 'bg-slate-200 dark:bg-slate-700'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          {currentStep === 'company' && (
            <motion.div
              key="company-selection"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6"
            >
              <div className="sqsft-card p-6">
                <h3 className="sqsft-heading-3 mb-4">
                  Select Company for Analysis
                </h3>
                
                <div className="space-y-4">
                  {demoCompanies.map((company) => (
                    <motion.div
                      key={company.id}
                      whileHover={{ scale: 1.02 }}
                      onClick={() => setSelectedCompany(company)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                        selectedCompany.id === company.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="sqsft-heading-4">
                          {company.name}
                        </h4>
                        <span className="sqsft-badge sqsft-badge-primary">
                          {company.industry}
                        </span>
                      </div>
                      <p className="sqsft-small" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                        {company.description}
                      </p>
                      <div className="flex items-center justify-between text-xs">
                        <span className="sqsft-caption">
                          {company.size} • {company.budget}
                        </span>
                        {selectedCompany.id === company.id && (
                          <CheckCircle className="w-4 h-4 text-blue-500" />
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleTRIZAnalysis}
                  className="sqsft-btn-primary w-full mt-6 flex items-center justify-center space-x-2"
                >
                  <Brain className="w-4 h-4" />
                  <span>Start TRIZ Analysis</span>
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </div>

              <div className="sqsft-card p-6">
                <h3 className="sqsft-heading-3 mb-4">
                  Company Profile
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="sqsft-heading-4 mb-2">
                      Current Challenges
                    </h4>
                    <div className="space-y-2">
                      {selectedCompany.currentChallenges.map((challenge, index) => (
                        <div key={index} className="flex items-start space-x-2">
                          <AlertTriangle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                          <span className="sqsft-small" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                            {challenge}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="sqsft-heading-4 mb-2">
                      Technology Stack
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedCompany.techStack.map((tech, index) => (
                        <span
                          key={index}
                          className="sqsft-badge sqsft-badge-primary"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="sqsft-heading-4 mb-2">
                      Key Competitors
                    </h4>
                    <div className="space-y-1">
                      {selectedCompany.competitors.map((competitor, index) => (
                        <div key={index} className="sqsft-small" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                          • {competitor}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {currentStep === 'analysis' && (
            <motion.div
              key="analysis"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.05 }}
              className="flex items-center justify-center h-96"
            >
              <div className="text-center space-y-6">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto"
                >
                  <Brain className="w-10 h-10 text-white" />
                </motion.div>
                
                <div>
                  <h3 className="text-2xl sqsft-heading-2 mb-2">
                    Analyzing Company Challenges
                  </h3>
                  <p className="sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                    AI is processing your company data and identifying innovation opportunities...
                  </p>
                </div>
                
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </motion.div>
          )}

          {currentStep === 'triz' && (
            <motion.div
              key="triz-results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              <div className="sqsft-card p-6">
                <h3 className="sqsft-heading-3 mb-4">
                  TRIZ Contradiction Analysis
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {contradictions.map((contradiction, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.2 }}
                      className="p-4 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 rounded-xl border border-red-200/50 dark:border-red-700/50"
                    >
                      <div className="flex items-center space-x-2 mb-3">
                        <Target className="w-5 h-5 text-red-600 dark:text-red-400" />
                        <span className="sqsft-heading-4">
                          Contradiction {index + 1}
                        </span>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-green-700 font-medium">Improving:</span>
                          <span className="sqsft-small ml-2" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                            {contradiction.improving}
                          </span>
                        </div>
                        <div>
                          <span className="text-red-700 font-medium">Worsening:</span>
                          <span className="sqsft-small ml-2" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                            {contradiction.worsening}
                          </span>
                        </div>
                        <div>
                          <span className="text-blue-700 font-medium">Principles:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {contradiction.principles.map(principleId => (
                              <span
                                key={principleId}
                                className="sqsft-badge sqsft-badge-primary"
                              >
                                #{principleId}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCurrentStep('features')}
                  className="sqsft-btn-primary mt-6 flex items-center space-x-2"
                >
                  <Lightbulb className="w-4 h-4" />
                  <span>Generate Innovative Features</span>
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </div>

              <div className="sqsft-card p-6">
                <h3 className="sqsft-heading-3 mb-4">
                  Applicable TRIZ Principles
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {trizPrinciples.slice(0, 3).map((principle, index) => (
                    <motion.div
                      key={principle.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl border border-purple-200/50 dark:border-purple-700/50"
                    >
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                          {principle.id}
                        </div>
                        <span className="sqsft-heading-4">
                          {principle.name}
                        </span>
                      </div>
                      
                      <p className="sqsft-small" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                        {principle.description}
                      </p>
                      
                      <div className="space-y-1">
                        {principle.examples.slice(0, 2).map((example, idx) => (
                          <div key={idx} className="sqsft-caption">
                            • {example}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {currentStep === 'features' && (
            <motion.div
              key="generated-features"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-2xl border border-green-200/50 dark:border-green-700/50 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-500 rounded-xl flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                        AI-Generated Features
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        Innovation breakthrough solutions based on TRIZ analysis
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-2xl font-bold gradient-text">
                      {innovationScore}/10
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      Innovation Score
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {generatedFeatures.map((feature, index) => (
                  <motion.div
                    key={feature.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6 card-hover"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">
                          {feature.name}
                        </h4>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                          {feature.description}
                        </p>
                      </div>
                      
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        feature.priority === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                        feature.priority === 'high' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400' :
                        'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                      }`}>
                        {feature.priority}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <div className="text-xs text-slate-500 dark:text-slate-500 mb-1">Impact</div>
                        <div className="flex items-center space-x-2">
                          <div className="w-16 bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-green-500 to-blue-500 h-2 rounded-full"
                              style={{ width: `${feature.impact * 10}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            {feature.impact}/10
                          </span>
                        </div>
                      </div>
                      
                      <div>
                        <div className="text-xs text-slate-500 dark:text-slate-500 mb-1">Effort</div>
                        <div className="flex items-center space-x-2">
                          <div className="w-16 bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-orange-500 to-red-500 h-2 rounded-full"
                              style={{ width: `${feature.effort * 10}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            {feature.effort}/10
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-4">
                        <span className="text-slate-600 dark:text-slate-400">
                          {feature.estimatedHours}h
                        </span>
                        <span className="text-slate-600 dark:text-slate-400">
                          {feature.quarter}
                        </span>
                      </div>
                      <span className="text-slate-600 dark:text-slate-400">
                        {feature.assignedTeam}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={generateRoadmap}
                className="btn-primary w-full flex items-center justify-center space-x-2 py-4"
              >
                <Calendar className="w-5 h-5" />
                <span>Generate Complete Roadmap</span>
                <ArrowRight className="w-5 h-5" />
              </motion.button>
            </motion.div>
          )}

          {currentStep === 'roadmap' && (
            <motion.div
              key="final-roadmap"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl border border-blue-200/50 dark:border-blue-700/50 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold gradient-text">
                        Roadmap Generated Successfully!
                      </h3>
                      <p className="text-slate-600 dark:text-slate-400">
                        AI-powered product roadmap for {selectedCompany.name}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="btn-secondary flex items-center space-x-2"
                    >
                      <Share className="w-4 h-4" />
                      <span>Share</span>
                    </motion.button>
                    
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="btn-primary flex items-center space-x-2"
                    >
                      <Download className="w-4 h-4" />
                      <span>Export</span>
                    </motion.button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                      {generatedFeatures.length}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      Innovative Features
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">
                      {innovationScore}/10
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      Innovation Score
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">
                      4
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      TRIZ Principles Applied
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6">
                  <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
                    Next Steps
                  </h4>
                  
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">1</div>
                      <span className="text-sm text-slate-700 dark:text-slate-300">Review and customize generated features</span>
                    </div>
                    
                    <div className="flex items-center space-x-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">2</div>
                      <span className="text-sm text-slate-700 dark:text-slate-300">Create detailed timeline and milestones</span>
                    </div>
                    
                    <div className="flex items-center space-x-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">3</div>
                      <span className="text-sm text-slate-700 dark:text-slate-300">Share with stakeholders for review</span>
                    </div>
                    
                    <div className="flex items-center space-x-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">4</div>
                      <span className="text-sm text-slate-700 dark:text-slate-300">Begin implementation planning</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6">
                  <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
                    Innovation Insights
                  </h4>
                  
                  <div className="space-y-4">
                    <div className="p-3 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <Lightbulb className="w-4 h-4 text-green-600 dark:text-green-400" />
                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          Key Innovation
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 dark:text-slate-400">
                        Modular architecture enables unprecedented scalability while maintaining code quality
                      </p>
                    </div>
                    
                    <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <Target className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          TRIZ Impact
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 dark:text-slate-400">
                        Applied 4 TRIZ principles to resolve critical contradictions in system design
                      </p>
                    </div>
                    
                    <div className="p-3 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          Business Value
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 dark:text-slate-400">
                        Expected 40% improvement in user engagement and 60% reduction in development complexity
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default RoadmapGenerator;